import React, { useState } from 'react';
import { App, Button, Card, Col, DatePicker, Descriptions, Progress, Row, Segmented, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, PaperClipOutlined, RollbackOutlined, StopOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import ReasonPrompt from './shared/ReasonPrompt';
import RecruitPeoplePicker from './shared/RecruitPeoplePicker';
import { fmtDate, OWNER_ROLE_LABEL, recruitErrorMessage, stageLabel, useRecruitAccess } from './shared/recruitUi';

const { Paragraph, Text } = Typography;

const isOverdue = (t: any) => t.status === 'Pending' && t.due_date && dayjs(t.due_date).isBefore(dayjs(), 'day');

/** Task status is Pending | Done | Skipped; plan status is Open | InProgress | Completed. */
const OnboardingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { canOnboard } = useRecruitAccess();
  const [busy, setBusy] = useState<string | null>(null);
  const [skipTask, setSkipTask] = useState<any>(null);
  const [filter, setFilter] = useState<'open' | 'all'>('all');

  const { data: plan, isLoading } = useQuery({
    queryKey: hrmsKeys.onboardingPlan(id || ''),
    queryFn: () => hrmsService.getOnboarding(id!),
    enabled: Boolean(id),
  });

  const act = async (taskId: string, fn: () => Promise<any>, ok: string) => {
    setBusy(taskId);
    try {
      await fn();
      message.success(ok);
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.onboardingPlan(id!) });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.onboarding() });
    } catch (e: any) {
      message.error(recruitErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Onboarding" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!plan) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Onboarding" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }

  const tasks: any[] = plan.tasks || [];
  const visible = filter === 'open' ? tasks.filter((t) => t.status === 'Pending') : tasks;
  const overdue = tasks.filter(isOverdue).length;
  const portalUrl = plan.portal_token && plan.status !== 'Completed' ? `${window.location.origin}/hr-onboarding/${plan.portal_token}` : null;

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Onboarding"
          documentNumber={plan.plan_number}
          subtitle={
            plan.employee ? (
              <Text type="secondary">
                <a onClick={() => navigate(`/hr/employees/${plan.employee.id}`)}>
                  {plan.employee.employee_number} — {plan.employee.full_name}
                </a>
                {plan.employee.department_name ? ` · ${plan.employee.department_name}` : ''}
              </Text>
            ) : undefined
          }
          statusTag={<Tag color={hrmsStatusColor(plan.status)}>{stageLabel(plan.status)}</Tag>}
          primaryActions={
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/onboarding')}>
              Back
            </Button>
          }
        />
      }
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={16}>
          <Card size="small" style={{ height: '100%' }}>
            <Progress percent={Number(plan.progress_pct) || 0} status={plan.status === 'Completed' ? 'success' : 'active'} />
            <Space size="large" wrap style={{ marginTop: 8 }}>
              <Text>
                {tasks.filter((t) => t.status !== 'Pending').length} of {tasks.length} tasks closed
              </Text>
              {overdue > 0 && <Tag color="red">{overdue} overdue</Tag>}
            </Space>
            {portalUrl && (
              <Paragraph copyable={{ text: portalUrl }} style={{ marginTop: 12, marginBottom: 0 }}>
                New-hire portal: <Text code>{portalUrl}</Text>
              </Paragraph>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card size="small" style={{ height: '100%' }}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Join date">{fmtDate(plan.join_date)}</Descriptions.Item>
              <Descriptions.Item label="Probation ends">{fmtDate(plan.probation_end_date)}</Descriptions.Item>
              <Descriptions.Item label="Target completion">{fmtDate(plan.due_date)}</Descriptions.Item>
              {plan.completed_at && <Descriptions.Item label="Completed">{fmtDate(plan.completed_at)}</Descriptions.Item>}
            </Descriptions>
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        title="Checklist"
        extra={
          <Segmented
            size="small"
            value={filter}
            onChange={(v) => setFilter(v as 'open' | 'all')}
            options={[
              { value: 'all', label: 'All' },
              { value: 'open', label: 'Open only' },
            ]}
          />
        }
        style={{ marginBottom: 16 }}
      >
        <Table
          rowKey="id"
          size="small"
          dataSource={visible}
          pagination={false}
          scroll={{ x: 1100 }}
          rowClassName={(t) => (isOverdue(t) ? 'ant-table-row-overdue' : '')}
          columns={[
            {
              title: 'Task',
              dataIndex: 'task_name',
              render: (v, t: any) => (
                <Space direction="vertical" size={0}>
                  <span>
                    {v} {t.is_mandatory ? null : <Tag>Optional</Tag>}
                    {t.document_id && (
                      <Tooltip title="Document uploaded">
                        <PaperClipOutlined />
                      </Tooltip>
                    )}
                  </span>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t.task_type}
                    {t.skip_reason ? ` · not applicable: ${t.skip_reason}` : ''}
                    {t.notes && !t.skip_reason ? ` · ${t.notes}` : ''}
                  </Text>
                </Space>
              ),
            },
            { title: 'Role', dataIndex: 'owner_role', width: 110, render: (r) => OWNER_ROLE_LABEL[r] || r || '—' },
            {
              title: 'Owner',
              key: 'owner',
              width: 220,
              render: (_, t: any) =>
                t.owner_role === 'NewHire' ? (
                  <Text type="secondary">New hire (portal)</Text>
                ) : canOnboard && t.status === 'Pending' ? (
                  <RecruitPeoplePicker
                    size="small"
                    style={{ width: '100%' }}
                    value={t.owner_employee_id}
                    placeholder="Assign owner"
                    onChange={(v) => act(t.id, () => hrmsService.updateOnboardingTask(id!, t.id, { owner_employee_id: (v as string) || null }), 'Owner updated')}
                  />
                ) : (
                  t.owner_name || <Text type="warning">Unassigned</Text>
                ),
            },
            {
              title: 'Due',
              dataIndex: 'due_date',
              width: 150,
              render: (v, t: any) =>
                canOnboard && t.status === 'Pending' ? (
                  <DatePicker
                    size="small"
                    format="DD MMM YYYY"
                    value={v ? dayjs(v) : null}
                    status={isOverdue(t) ? 'error' : undefined}
                    onChange={(d) => act(t.id, () => hrmsService.updateOnboardingTask(id!, t.id, { due_date: d ? d.format('YYYY-MM-DD') : null }), 'Due date updated')}
                  />
                ) : (
                  <span style={{ color: isOverdue(t) ? '#cf1322' : undefined }}>{fmtDate(v)}</span>
                ),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 110,
              render: (s: string, t: any) => (
                <Tag color={s === 'Done' ? 'green' : s === 'Skipped' ? 'default' : isOverdue(t) ? 'red' : 'blue'}>{s === 'Skipped' ? 'Not applicable' : isOverdue(t) ? 'Overdue' : s}</Tag>
              ),
            },
            {
              title: '',
              key: 'actions',
              width: 200,
              render: (_, t: any) =>
                t.status === 'Pending' ? (
                  <Space size={0}>
                    <Button
                      type="link"
                      size="small"
                      icon={<CheckOutlined />}
                      loading={busy === t.id}
                      onClick={() => act(t.id, () => hrmsService.completeOnboardingTask(id!, t.id), 'Task done')}
                    >
                      Done
                    </Button>
                    {canOnboard && (
                      <Button type="link" size="small" icon={<StopOutlined />} onClick={() => setSkipTask(t)}>
                        N/A
                      </Button>
                    )}
                  </Space>
                ) : canOnboard ? (
                  <Button
                    type="link"
                    size="small"
                    icon={<RollbackOutlined />}
                    loading={busy === t.id}
                    onClick={() => act(t.id, () => hrmsService.reopenOnboardingTask(id!, t.id), 'Task reopened')}
                  >
                    Reopen
                  </Button>
                ) : null,
            },
          ]}
        />
      </Card>
      <EntityAttachmentsCard entityType="HR_ONB" entityId={plan.id} title="Onboarding documents" uploadHint="Passport, visa, Emirates ID, labour card, signed policies" />

      <ReasonPrompt
        open={Boolean(skipTask)}
        title={`Mark "${skipTask?.task_name || ''}" not applicable`}
        okText="Mark not applicable"
        notesRequired
        notesLabel="Reason"
        danger={false}
        onCancel={() => setSkipTask(null)}
        onSubmit={async (v) => {
          const t = skipTask;
          setSkipTask(null);
          await act(t.id, () => hrmsService.skipOnboardingTask(id!, t.id, v.notes || ''), 'Marked not applicable');
        }}
      />
    </WorkspaceLayout>
  );
};

export default OnboardingDetailPage;
